import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { TrendingUp, Home, Users } from 'lucide-react';

export default function GrowthTab({ growthStats }) {
  return (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-400" />
            Platform Growth
          </CardTitle>
          <CardDescription className="text-slate-400">
            Track families, members, and user signups across Legacy Forge.
          </CardDescription>
        </CardHeader>
      </Card>

      {growthStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400" data-testid="stat-families">{growthStats.total_families}</p>
              <p className="text-slate-400 text-sm mt-1">Families</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400" data-testid="stat-users">{growthStats.total_users}</p>
              <p className="text-slate-400 text-sm mt-1">Registered Users</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-400" data-testid="stat-members">{growthStats.total_unique_members}</p>
              <p className="text-slate-400 text-sm mt-1">Family Members</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400" data-testid="stat-proxies">{growthStats.total_proxies}</p>
              <p className="text-slate-400 text-sm mt-1">Proxy Members</p>
            </CardContent>
          </Card>
        </div>
      )}

      {growthStats && growthStats.recent_families.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Home className="h-4 w-4 text-green-400" />
              Recent Families (Last 30 Days)
            </h3>
            <div className="space-y-3">
              {growthStats.recent_families.map((fam, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-900 rounded-lg px-4 py-3 border border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-600/20 flex items-center justify-center">
                      <Home className="h-4 w-4 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{fam.name}</p>
                      <p className="text-slate-500 text-xs">
                        Created by {fam.created_by_name}
                        <span className="text-slate-600 mx-1">|</span>
                        {fam.member_count} member{fam.member_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <span className="text-slate-500 text-xs">
                    {new Date(fam.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {growthStats && growthStats.recent_users.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              Recent User Signups (Last 30 Days)
            </h3>
            <div className="space-y-3">
              {growthStats.recent_users.map((user, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-900 rounded-lg px-4 py-3 border border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center">
                      <Users className="h-4 w-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{user.name}</p>
                      <p className="text-slate-500 text-xs">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.account_type === 'professional' && (
                      <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30">Pro</Badge>
                    )}
                    <span className="text-slate-500 text-xs">
                      {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {growthStats && growthStats.recent_families.length === 0 && growthStats.recent_users.length === 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <TrendingUp className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No new activity in the last 30 days. Share the platform to start growing!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
